import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { EventWithDetails } from "@/types/database";

/**
 * Fetch the current active (published) event with all related data.
 * Returns the next upcoming published event.
 */
export function useActiveEvent() {
  return useQuery<EventWithDetails | null>({
    queryKey: ["active-event"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select(
          `
          *,
          ticket_tiers(*),
          table_packages(*),
          bottle_menu(*),
          add_ons(*),
          mixers(*)
        `
        )
        .eq("status", "published")
        .order("date", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as EventWithDetails | null;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch a specific event by slug.
 */
export function useEventBySlug(slug: string) {
  return useQuery<EventWithDetails | null>({
    queryKey: ["event", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select(
          `
          *,
          ticket_tiers(*),
          table_packages(*),
          bottle_menu(*),
          add_ons(*),
          mixers(*)
        `
        )
        .eq("slug", slug)
        .maybeSingle();

      if (error) throw error;
      return data as EventWithDetails | null;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5,
  });
}
